import React, { useState, useEffect } from "react";
import { getFirestore, collection, onSnapshot, query, where, orderBy, limit, getDocs, doc, getDoc } from "firebase/firestore";
import app, { systemAdministratorAuth } from "../../firebase";
import { formatCalendarDate, getCalendarRangeStatus, isCalendarDateInRange } from "../../utils/announcementDates";
import AdminShell from "./AdminShell";

export default function AdminDashboard() {
  const [now, setNow] = useState(new Date());
  const [adminProfile, setAdminProfile] = useState({});
  const [adminProfileLoaded, setAdminProfileLoaded] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Real-time data state
  const [loadingData, setLoadingData] = useState(true);
  const [counts, setCounts] = useState({
    users: 0,
    students: 0,
    professors: 0,
    deans: 0,
    programHeads: 0,
    admissions: 0,
    registrars: 0,
    roomManagers: 0,
    departments: 0,
    programs: 0,
    pendingAccounts: 0,
    activeAccounts: 0
  });
  const [liveActivities, setLiveActivities] = useState([]);
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [announcementData, setAnnouncementData] = useState(null);
  const [dataError, setDataError] = useState("");

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    return formatCalendarDate(dateStr) || "—";
  };

  const formatDateRange = (start, end) => {
    if (!start && !end) return "—";
    if (!start) return `Until ${formatDisplayDate(end)}`;
    if (!end) return `From ${formatDisplayDate(start)}`;
    return `${formatDisplayDate(start)} — ${formatDisplayDate(end)}`;
  };

  const getSemesterStatus = (startDate, endDate) => {
    return getCalendarRangeStatus(startDate, endDate, now);
  };

  const getOpenClosedStatus = (isOpen) => isOpen ? "OPEN" : "CLOSED";
  const getStatusBadgeStyle = (status) => ({
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    background: status === 'Ongoing' || status === 'OPEN' ? '#dcfce7' : status === 'Ended' || status === 'CLOSED' ? '#e2e8f0' : '#fef3c7',
    color: status === 'Ongoing' || status === 'OPEN' ? '#166534' : status === 'Ended' || status === 'CLOSED' ? '#475569' : '#92400e',
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0.3
  });

  const isEnrollmentWindowOpen = (startDate, endDate) => {
    return isCalendarDateInRange(startDate, endDate, now);
  };

  useEffect(() => {
    const db = getFirestore(app);
    const auth = systemAdministratorAuth;
    let active = true;
    const loadAdminProfile = async () => {
      setAdminProfileLoaded(false);
      try {
        if (auth.currentUser) {
          const snapshot = await getDoc(doc(db, "users", auth.currentUser.uid));
          if (active) setAdminProfile(snapshot.exists() ? snapshot.data() : {});
        } else if (active) {
          setAdminProfile({});
        }
      } catch (error) {
        console.error("Failed to load administrator dashboard profile", error);
        if (active) setAdminProfile({});
      } finally {
        if (active) setAdminProfileLoaded(true);
      }
    };
    loadAdminProfile();
    const unsubscribes = [];
    setLoadingData(true);
    setDataError("");

    try {
      // Total users
      const usersCol = collection(db, "users");
      unsubscribes.push(onSnapshot(usersCol, (snap) => {
        setCounts(prev => ({ ...prev, users: snap.size }));
      }, (err) => { console.error('users listen error', err); setDataError('Failed to load users'); }));

      // Students
      const studentsCol = collection(db, "students");
      unsubscribes.push(onSnapshot(studentsCol, (snap) => {
        setCounts(prev => ({ ...prev, students: snap.size }));
      }, (err) => { console.error('students listen error', err); setDataError('Failed to load students'); }));

      // Departments
      const departmentsCol = collection(db, "departments");
      unsubscribes.push(onSnapshot(departmentsCol, (snap) => {
        setCounts(prev => ({ ...prev, departments: snap.size }));
      }, (err) => { console.info('departments missing or empty', err); setCounts(prev => ({ ...prev, departments: 0 })); }));

      // Programs - try 'programs' collection, fallback to 'curriculums' unique count
      const programsCol = collection(db, "programs");
      const unsubPrograms = onSnapshot(programsCol, async (snap) => {
        if (snap.size > 0) {
          setCounts(prev => ({ ...prev, programs: snap.size }));
        } else {
          // fallback: try curriculums collection for distinct program names
          try {
            const curr = collection(db, 'curriculums');
            const currSnap = await getDocs(curr);
            const unique = new Set(currSnap.docs.map(d => (d.data().programName || d.data().name || '').trim()).filter(Boolean));
            setCounts(prev => ({ ...prev, programs: unique.size }));
          } catch (e) {
            setCounts(prev => ({ ...prev, programs: 0 }));
          }
        }
      }, (err) => { console.info('programs listen error', err); setCounts(prev => ({ ...prev, programs: 0 })); });
      unsubscribes.push(unsubPrograms);

      // Role-based counts
      const roleCount = async (role, key) => {
        try {
          let q;
          if (Array.isArray(role)) {
            // Use 'in' for multiple role variants (max 10 elements)
            q = query(collection(db, 'users'), where('role', 'in', role));
          } else {
            q = query(collection(db, 'users'), where('role', '==', role));
          }
          const snapUnsub = onSnapshot(q, (snap) => {
            setCounts(prev => ({ ...prev, [key]: snap.size }));
          }, (err) => { console.error(`${role} listen error`, err); setCounts(prev => ({ ...prev, [key]: 0 })); });
          unsubscribes.push(snapUnsub);
        } catch (e) {
          setCounts(prev => ({ ...prev, [key]: 0 }));
        }
      };

      roleCount('Professor', 'professors');
      roleCount('Dean', 'deans');
      roleCount('Program Head', 'programHeads');
      // Count both 'Admission' and 'Admission Counselor' variants
      roleCount(['Admission', 'Admission Counselor'], 'admissions');
      roleCount('Registrar', 'registrars');
      roleCount('Room Manager', 'roomManagers');

      // Pending accounts - users with status 'pending' and students with appointment.status 'pending'
      try {
        const pendingUsersQ = query(collection(db, 'users'), where('status', '==', 'pending'));
        unsubscribes.push(onSnapshot(pendingUsersQ, (snap) => {
          const pendingUsers = snap.size;
          // students pending
          const pendingStudentsQ = query(collection(db, 'students'), where('appointment.status', '==', 'pending'));
          const unsubStudents = onSnapshot(pendingStudentsQ, (s2) => {
            const pendingStudents = s2.size;
            setCounts(prev => ({ ...prev, pendingAccounts: pendingUsers + pendingStudents }));
          }, (err) => {
            console.error('pending students listen error', err);
            setCounts(prev => ({ ...prev, pendingAccounts: pendingUsers }));
          });
          unsubscribes.push(unsubStudents);
        }, (err) => { console.error('pending users listen error', err); setCounts(prev => ({ ...prev, pendingAccounts: 0 })); }));
      } catch (e) {
        setCounts(prev => ({ ...prev, pendingAccounts: 0 }));
      }

      // Active accounts - users with status 'active'
      try {
        const activeQ = query(collection(db, 'users'), where('status', '==', 'active'));
        unsubscribes.push(onSnapshot(activeQ, (snap) => {
          setCounts(prev => ({ ...prev, activeAccounts: snap.size }));
        }, (err) => { console.error('active users listen error', err); setCounts(prev => ({ ...prev, activeAccounts: 0 })); }));
      } catch (e) {
        setCounts(prev => ({ ...prev, activeAccounts: 0 }));
      }

      // Recent activities - listen to common collection names, merge and sort
      const activityCollections = ['activities', 'activityLogs', 'auditLogs', 'audit', 'logs'];
      const activityDocs = [];
      activityCollections.forEach(name => {
        try {
          const col = collection(db, name);
          const unsub = onSnapshot(query(col, orderBy('timestamp', 'desc'), limit(30)), (snap) => {
            if (!snap.empty) {
              const rows = snap.docs.map(d => ({ id: d.id, ...(d.data()) }));
              // merge into activityDocs by id
              rows.forEach(r => {
                const idx = activityDocs.findIndex(x => x.id === r.id);
                if (idx === -1) activityDocs.push(r);
                else activityDocs[idx] = r;
              });
              // sort by timestamp desc
              activityDocs.sort((a, b) => (b.timestamp && a.timestamp) ? (b.timestamp.seconds || b.timestamp) - (a.timestamp.seconds || a.timestamp) : 0);
              setLiveActivities([...activityDocs].slice(0, 50));
            }
          }, (err) => { /* ignore missing collection errors */ });
          unsubscribes.push(unsub);
        } catch (e) {
          // ignore
        }
      });

      // Notifications: try 'notifications' collection and 'systemSettings/announcement' doc
      try {
        const notifCol = collection(db, 'notifications');
        const unsubNotif = onSnapshot(notifCol, (snap) => {
          setLiveNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (err) => { console.info('notifications collection missing', err); });
        unsubscribes.push(unsubNotif);
      } catch (e) {}

      try {
        const sysRef = doc(db, 'systemSettings', 'announcement');
        const unsubSys = onSnapshot(sysRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            setAnnouncementData(data);
            const messages = [];
            if (data.pendingAccounts) messages.push(`${data.pendingAccounts} Pending User Accounts`);
            if (data.passwordResetRequests) messages.push(`${data.passwordResetRequests} Password Reset Requests`);
            if (data.schoolYearStart && data.schoolYearEnd) messages.push(`Academic Year ${data.schoolYearStart}-${data.schoolYearEnd} Active`);
            if (data.announcement) messages.push(data.announcement);
            setLiveNotifications(prev => {
              // merge without duplicates
              const merged = [...messages.map((m, i) => ({ id: `sys-${i}`, message: m })), ...prev.filter(p => !p.id?.startsWith('sys-'))];
              return merged;
            });
          } else {
            setAnnouncementData(null);
          }
        }, (err) => { console.info('systemSettings doc missing', err); setAnnouncementData(null); });
        unsubscribes.push(unsubSys);
      } catch (e) {}

      setLoadingData(false);
    } catch (e) {
      console.error('Admin dashboard data load error', e);
      setDataError('Failed to initialize listeners');
      setLoadingData(false);
    }

    return () => {
      active = false;
      unsubscribes.forEach(u => { try { u(); } catch (e) { } });
    };
  }, []);

  // derive stats array from counts for rendering
  const liveStats = [
    { label: 'Total Users', value: counts.users },
    { label: 'Students', value: counts.students },
    { label: 'Professors', value: counts.professors },
    { label: 'Deans', value: counts.deans },
    { label: 'Program Heads', value: counts.programHeads },
    { label: 'Admissions', value: counts.admissions },
    { label: 'Registrars', value: counts.registrars },
    { label: 'Room Managers', value: counts.roomManagers },
    { label: 'Departments', value: counts.departments }
  ];

  const activeUsers = counts.activeAccounts || 0;
  const inactiveUsers = Math.max((counts.users || 0) - activeUsers, 0);
  const adminName = adminProfile.fullName || adminProfile.name ||
    [adminProfile.firstName, adminProfile.middleName, adminProfile.lastName].filter(Boolean).join(" ").trim() ||
    (adminProfileLoaded
      ? systemAdministratorAuth.currentUser?.displayName || systemAdministratorAuth.currentUser?.email?.split("@")[0]
      : "");
  const adminRole = adminProfile.role || "System Administrator";
  const activeAcademicTerm = [
    {
      term: "1st Semester",
      schoolYearStart: announcementData?.firstSemYearStart,
      schoolYearEnd: announcementData?.firstSemYearEnd,
      start: announcementData?.firstSemStart,
      end: announcementData?.firstSemEnd
    },
    {
      term: "2nd Semester",
      schoolYearStart: announcementData?.secondSemYearStart,
      schoolYearEnd: announcementData?.secondSemYearEnd,
      start: announcementData?.secondSemStart,
      end: announcementData?.secondSemEnd
    }
  ].find(semester => isCalendarDateInRange(semester.start, semester.end, now));
  const academicTermIndicator = activeAcademicTerm
    ? `${activeAcademicTerm.schoolYearStart && activeAcademicTerm.schoolYearEnd
      ? `AY ${activeAcademicTerm.schoolYearStart}-${activeAcademicTerm.schoolYearEnd}`
      : "School year not configured"} · ${activeAcademicTerm.term}`
    : "No active term";

  return (
    <AdminShell title="Dashboard">
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ flex: 1 }}>
              <section style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24, padding: 28, marginBottom: 28, background: '#155c2b', color: '#fff', borderRadius: 10 }}>
                <div>
                  <div style={{ marginBottom: 8, color: '#c9e6d1', fontSize: 13, fontWeight: 700, textTransform: 'uppercase' }}>Administrator Dashboard</div>
                  {adminName && <h2 style={{ margin: '0 0 10px', fontSize: 30 }}>Welcome, {adminName}</h2>}
                  <div style={{ fontSize: 16 }}>Role: {adminRole}</div>
                  <div style={{ marginTop: 8, color: '#dceee1', fontSize: 14, fontWeight: 600 }}>
                    SY / Term: {academicTermIndicator}
                  </div>
                </div>
                <div style={{ minWidth: 230, padding: '16px 20px', background: '#fff', color: '#222', borderRadius: 8 }}>
                  <div style={{ marginBottom: 6, color: '#666', fontSize: 13 }}>Today</div>
                  <time dateTime={now.toISOString()} style={{ fontSize: 16, fontWeight: 600 }}>
                    {now.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </time>
                  <div style={{ marginTop: 6, color: '#155c2b', fontSize: 22, fontWeight: 700 }}>
                    {now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
              </section>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
                {liveStats.map(s => (
                  <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{s.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#155c2b' }}>{loadingData ? '—' : (typeof s.value === 'number' ? s.value : (s.value ?? '—'))}</div>
                  </div>
                ))}
              </div>

              

              <div style={{ background: '#f4f7fb', border: '1px solid #d1d5db', borderRadius: 12, padding: 24, marginBottom: 18 }}>
                <div style={{ fontWeight: 800, fontSize: 20, color: '#0f172a', marginBottom: 8 }}>Notification Center</div>
                <div style={{ color: '#475569', fontSize: 15, marginBottom: 18 }}>System announcement, semester progress, and enrollment window details.</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14 }}>
                  <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#1d4ed8', marginBottom: 10 }}>Current System Announcement</div>
                    <div style={{ color: '#334155', fontSize: 16, lineHeight: 1.7 }}>
                      {announcementData?.announcement || 'No announcement has been configured yet.'}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 14 }}>1st Semester</div>
                      <div style={{ fontSize: 15, color: '#475569', marginBottom: 8 }}>Semester status: <span style={getStatusBadgeStyle(getSemesterStatus(announcementData?.firstSemStart, announcementData?.firstSemEnd))}>{getSemesterStatus(announcementData?.firstSemStart, announcementData?.firstSemEnd)}</span></div>
                      <div style={{ fontSize: 14, color: '#334155', marginBottom: 12 }}>Semester dates: <strong>{formatDateRange(announcementData?.firstSemStart, announcementData?.firstSemEnd)}</strong></div>
                      <div style={{ fontSize: 15, color: '#475569', marginBottom: 6 }}>Enrollment: <span style={getStatusBadgeStyle(getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.firstSemEnrollmentStart, announcementData?.firstSemEnrollmentEnd)))}>{getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.firstSemEnrollmentStart, announcementData?.firstSemEnrollmentEnd))}</span></div>
                      <div style={{ fontSize: 14, color: '#334155', marginBottom: 12 }}>Window: <strong>{formatDateRange(announcementData?.firstSemEnrollmentStart, announcementData?.firstSemEnrollmentEnd)}</strong></div>
                      <div style={{ fontSize: 15, color: '#475569', marginBottom: 6 }}>Appointment: <span style={getStatusBadgeStyle(getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.firstSemAppointmentStart, announcementData?.firstSemAppointmentEnd)))}>{getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.firstSemAppointmentStart, announcementData?.firstSemAppointmentEnd))}</span></div>
                      <div style={{ fontSize: 14, color: '#334155' }}>Window: <strong>{formatDateRange(announcementData?.firstSemAppointmentStart, announcementData?.firstSemAppointmentEnd)}</strong></div>
                    </div>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}>
                      <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 14 }}>2nd Semester</div>
                      <div style={{ fontSize: 15, color: '#475569', marginBottom: 8 }}>Semester status: <span style={getStatusBadgeStyle(getSemesterStatus(announcementData?.secondSemStart, announcementData?.secondSemEnd))}>{getSemesterStatus(announcementData?.secondSemStart, announcementData?.secondSemEnd)}</span></div>
                      <div style={{ fontSize: 14, color: '#334155', marginBottom: 12 }}>Semester dates: <strong>{formatDateRange(announcementData?.secondSemStart, announcementData?.secondSemEnd)}</strong></div>
                      <div style={{ fontSize: 15, color: '#475569', marginBottom: 6 }}>Enrollment: <span style={getStatusBadgeStyle(getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.secondSemEnrollmentStart, announcementData?.secondSemEnrollmentEnd)))}>{getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.secondSemEnrollmentStart, announcementData?.secondSemEnrollmentEnd))}</span></div>
                      <div style={{ fontSize: 14, color: '#334155', marginBottom: 12 }}>Window: <strong>{formatDateRange(announcementData?.secondSemEnrollmentStart, announcementData?.secondSemEnrollmentEnd)}</strong></div>
                      <div style={{ fontSize: 15, color: '#475569', marginBottom: 6 }}>Appointment: <span style={getStatusBadgeStyle(getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.secondSemAppointmentStart, announcementData?.secondSemAppointmentEnd)))}>{getOpenClosedStatus(isEnrollmentWindowOpen(announcementData?.secondSemAppointmentStart, announcementData?.secondSemAppointmentEnd))}</span></div>
                      <div style={{ fontSize: 14, color: '#334155' }}>Window: <strong>{formatDateRange(announcementData?.secondSemAppointmentStart, announcementData?.secondSemAppointmentEnd)}</strong></div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>
    </AdminShell>
  );
}
